// One read path. These types mirror the API in src/server.ts exactly; the
// dashboard must never derive a figure the API doesn't serve (CLAUDE.md:
// "Dashboard, MCP server, and API are THE SAME surface").

import { getSession, clearSession, type Session, type MembershipSummary } from "./session";
import { go } from "./router";
import { API_BASE as BASE } from "./lib/apiBase";

let inflight = 0;
const inflightListeners = new Set<(n: number) => void>();

function beginRequest(): void {
  inflight += 1;
  for (const listener of inflightListeners) listener(inflight);
}
function endRequestCounted(): void {
  inflight = Math.max(0, inflight - 1);
  for (const listener of inflightListeners) listener(inflight);
}

/** Subscribe to the number of in-flight API requests (global progress bar). */
export function subscribeInflight(fn: (n: number) => void): () => void {
  inflightListeners.add(fn);
  fn(inflight);
  return () => { inflightListeners.delete(fn); };
}

export type ProductRow = {
  product_id: string;
  slug: string;
  name: string;
  stage: "validation" | "live" | "killed";
  currency: string;
  domain: string | null;
  gate_date: string | null;
  gate_result: "pass" | "fail" | "pivot" | null;
  gate_due_date: string | null;
  spend_7d_cents: number;
  spend_30d_cents: number;
  spend_prev_30d_cents: number;
  visits_30d: number;
  visits_prev_30d: number;
  leads_30d: number;
  leads_prev_30d: number;
  revenue_30d_cents: number;
  revenue_prev_30d_cents: number;
  customers_30d: number;
  customers_prev_30d: number;
  cac_cents: number | null;
  ltv_cents: number | null;
  ltv_cac: number | null;
  pending_cards: number;
};

export type QueueCard = {
  id: string;
  product_id: string | null;
  product_slug: string | null;
  product_name: string | null;
  card_type: "proposal" | "insight" | "alert" | "gate_result" | "policy_block";
  headline: string;
  metric_line: string;
  detail: string | null;
  proposed_by: "keeper" | "system";
  action_spec: ActionSpec | null;
  status: "pending" | "approved" | "rejected" | "snoozed" | "expired" | "executed" | "failed";
  snoozed_until: string | null;
  expires_at: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
};

export type ActionSpec = {
  op?: string;
  network?: string;
  from_cents?: number;
  to_cents?: number;
  expected_impact?: string;
  [k: string]: unknown;
};

export type FunnelRow = { stage: string; channel: string | null; events: number; volume: number };

export type CohortRow = {
  channel: string;
  cohort_month: string;
  day_n: number;
  identities: number;
  cum_revenue_cents: number;
};

export type ChannelCac = {
  channel: string;
  spend_cents: number;
  customers: number;
  cac_cents: number | null;
};

export type Payback = { cohorts: CohortRow[]; cac: ChannelCac[] };

export type ScorecardFormat = "int" | "cents" | "percent";
export type ScorecardKind = "auto" | "manual" | "derived";
export type ScorecardMetric = {
  key: string;
  label: string;
  kind: ScorecardKind;
  metric_ref?: string;
  formula?: string;
  format: ScorecardFormat;
  target?: number | null;
};
export type ScorecardCatalogEntry = ScorecardMetric & {
  sectionId: string;
  sectionLabel: string;
  description: string;
};
export type ScorecardView = {
  product_slug: string;
  product_name: string;
  currency: string;
  weeks: Array<{ week_start: string; week_end: string; label: string }>;
  sections: Array<{
    id: string;
    label: string;
    metrics: Array<ScorecardMetric & {
      source: string | null;
      values: Array<{ week_start: string; value: number | null }>;
      wow: number | null;
    }>;
  }>;
  template: string | null;
  spreadsheet_id: string | null;
  share_url: string | null;
  sheets_enabled: boolean;
  catalog: ScorecardCatalogEntry[];
  clamped: boolean;
};
export type ScorecardHighlight = {
  metric_key: string;
  week_start: string;
  kind: "good" | "bad" | "watch";
  note: string;
};
export type ScorecardRailCard = {
  type: "insight" | "query" | "action";
  headline: string;
  metric_line: string;
  detail: string;
  metric_key: string | null;
};
export type ScorecardAnalysis = {
  highlights: ScorecardHighlight[];
  cards: ScorecardRailCard[];
  ai: boolean;
  model: string | null;
  generated_at: string;
};
export type ScorecardSuggest = {
  metrics: ScorecardMetric[];
  matched: string[];
  notes: Array<{ key: string; reason: string }>;
  model: string | null;
};

export type JobHealth = {
  name: string;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_ok: boolean | null;
  last_error: string | null;
  last_success_at: string | null;
  consecutive_failures: number;
};

export type SourceHealth = {
  fact: "events" | "transactions" | "spend_records";
  source: string;
  latest_fact_at: string | null;
  latest_ingest_at: string | null;
  rows_7d: number;
  rows_total: number;
};

export type Health = {
  jobs: JobHealth[];
  sources: SourceHealth[];
  queue: {
    pending: number;
    snoozed: number;
    oldest_pending_at: string | null;
    past_expiry: number;
  } | null;
  attribution: Array<{
    product_slug: string;
    revenue_30d_cents: number;
    unattributed_30d_cents: number;
  }>;
  products: Array<{ stage: string; n: number }>;
  now: string;
};

export type Connection = {
  id: string;
  kind: string;
  status: "pending" | "active" | "error" | "disabled";
  config: Record<string, unknown> | null;
  secret_ref: string | null;
  credential_id: string | null;
  consecutive_failures: number;
  next_retry_at: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  /** How deep the staged backfill has read: 0 never, then 30, 365, 3650 (all). */
  backfill_days: number;
  /** A run is happening right now. Poll while true — the figures are still moving. */
  syncing: boolean;
  /** What that run is doing, for a human. Null when idle. */
  sync_phase: string | null;
  created_at: string;
  product_slug: string;
  product_name: string;
};

// ---- Redesign surface: dashboards, attribution, funnel design, health, history ----

export const STAGES = [
  "impression", "visit", "engaged", "lead", "qualified",
  "signup", "activated", "converted", "payment", "churned",
] as const;
export type Stage = (typeof STAGES)[number];

export const STAGE_LABELS: Record<Stage, string> = {
  impression: "Impressions", visit: "Visits", engaged: "Engaged", lead: "Leads",
  qualified: "Qualified", signup: "Signups", activated: "Activated",
  converted: "First payment", payment: "Repeat", churned: "Churned",
};

export type MetricKey =
  | "spend_30d" | "revenue_30d" | "customers_30d" | "cac" | "ltv" | "ltv_cac"
  | "visits_30d" | "leads_30d" | "pending_cards"
  | "search_clicks" | "search_impressions" | "ai_visibility"
  | "social_followers" | "social_reach" | "social_engagement_rate";
export type SeriesKey = "spend" | "revenue" | "visits" | "leads" | "customers"
  | "search_clicks" | "search_impressions" | "ai_visibility"
  | "social_impressions" | "social_engagement" | "social_followers";
export type WidgetSize = "xs" | "sm" | "md" | "lg";

/** Grid placement in 12-column / 32px-row units. Absent = auto-packed. */
export type WidgetLayout = { x: number; y: number; w: number; h: number };

export type Widget = { id: string; title?: string; size?: WidgetSize; layout?: WidgetLayout } & (
  | { type: "metric"; metric: MetricKey }
  | { type: "timeseries"; series: SeriesKey; days: number }
  | { type: "funnel"; days: number }
  | { type: "payback" }
  | { type: "queue" }
  | { type: "breakdown"; measure: "spend" | "revenue_first" | "revenue_last"; days: number }
  | { type: "ad_breakdown"; level: AdLevel; measure: "roas" | "spend" | "revenue_first"; days: number }
  | { type: "top_social_posts"; days: number }
  | { type: "note"; text: string }
);

export type DashboardSpec = { widgets: Widget[] };

export type Dashboard = {
  id: string;
  product_slug: string;
  name: string;
  description: string | null;
  spec: DashboardSpec;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type GeneratedDashboard = { name: string; spec: DashboardSpec; matched: string[] };

export type ConversionScoreMode = "none" | "grade" | "numeric";

export type AttributionChannel = {
  channel: string;
  spend_cents: number;
  revenue_first_cents: number;
  revenue_last_cents: number;
  transactions_first: number;
  customers_first: number;
  conversions_first?: number;
  conversions_last?: number;
  cpa_cents?: number | null;
  cpa_first_cents?: number | null;
  grade_a_first?: number;
  avg_score_first?: number | null;
  cac_cents: number | null;
  roas: number | null;
};

export type AttributionReport = {
  window_days: number;
  channels: AttributionChannel[];
  totals: {
    revenue_cents: number;
    spend_cents: number;
    attributed_first_cents: number;
    unattributed_cents: number;
    conversions_first?: number;
    blended_cpa_cents?: number | null;
  };
  primary_goal?: {
    key: string;
    label: string;
    stage: string;
    score_mode: ConversionScoreMode;
    value_mode: ConversionValueMode;
    target_cpa_cents: number | null;
  } | null;
  coverage: {
    identities_total: number;
    identities_with_channel: number;
    events_total: number;
    events_with_channel: number;
    transactions_total: number;
    transactions_with_identity: number;
  };
};

export type AttributionSale = {
  id: string;
  occurred_at: string;
  kind: "charge" | "usage" | "invoice" | "refund" | "dispute" | "adjustment";
  amount_cents: number;
  currency: string;
  source: string;
  source_ref: string;
  identity_id: string | null;
  email: string | null;
  external_id: string | null;
  first_channel: string;
  last_channel: string;
  first_utm: Record<string, unknown> | null;
};

export type AttributionSales = {
  window_days: number;
  total: number;
  sales: AttributionSale[];
};

export const AD_LEVELS = ["campaign", "ad_group", "keyword", "ad"] as const;
export type AdLevel = (typeof AD_LEVELS)[number];

export const AD_LEVEL_LABELS: Record<AdLevel, string> = {
  campaign: "Campaign", ad_group: "Ad group", keyword: "Keyword", ad: "Creative",
};

export type AdPerformanceRow = {
  key: string;
  campaign_id: string | null;
  campaign_name: string | null;
  ad_group_name: string | null;
  label: string | null;
  match_type: string | null;
  currency: string | null;
  spend_cents: number;
  impressions: number;
  clicks: number;
  revenue_first_cents: number;
  revenue_last_cents: number;
  transactions_first: number;
  customers_first: number;
  cac_cents: number | null;
  roas: number | null;
};

export type AdPerformanceReport = {
  window_days: number;
  level: AdLevel;
  rows: AdPerformanceRow[];
  totals: {
    spend_cents: number;
    detail_spend_cents: number;
    spend_without_detail_cents: number;
    revenue_cents: number;
    attributed_first_cents: number;
    unattributed_cents: number;
    no_dimension_cents: number;
  };
  coverage: {
    paid_identities: number;
    identities_with_ad: number;
    identities_with_dimension: number;
  };
};

export type AdsUrlSuffixStatus = {
  connected: boolean;
  customer_id: string | null;
  final_url_suffix: string | null;
  auto_tagging_enabled: boolean;
  complete: boolean;
  missing: string[];
  proposed_suffix: string;
  recommended_suffix: string;
  deployed_at: string | null;
  /** Set when the live account read was impossible; the other fields then only
   *  describe what we would add. */
  read_error: string | null;
};

export type TimeseriesRow = {
  day: string;
  spend_cents: number;
  revenue_cents: number;
  visits: number;
  leads: number;
  customers: number;
  search_clicks: number;
  search_impressions: number;
  ai_visibility: number | null;
  social_impressions: number;
  social_engagement: number;
  social_followers: number;
};

export type DemandReport = {
  product_slug: string;
  window_days: number;
  funnel: {
    impressions: number;
    clicks: number;
    visits: number;
    leads: number;
    prev: { impressions: number; clicks: number; visits: number; leads: number };
  };
  trend: Array<{ day: string; impressions: number; clicks: number; visits: number; leads: number }>;
  queries: Array<{
    query: string;
    clicks: number;
    impressions: number;
    position: number | null;
    ctr: number;
    flag: "striking_distance" | "low_ctr" | "declining" | "none";
  }>;
  ranks: Array<{
    keyword: string;
    position: number | null;
    volume: number | null;
    url: string | null;
    source: string;
  }>;
  ai_visibility: { score: number | null; mention_rate: number | null; taken_at: string | null };
  authority: {
    domain_rating: number | null;
    referring_domains: number | null;
    organic_keywords: number | null;
    source: string | null;
    taken_at: string | null;
  };
  sources: Array<{
    kind: "gsc" | "bing" | "semrush" | "ahrefs" | "opinly";
    label: string;
    connected: boolean;
    status: string | null;
  }>;
};

export type SocialReport = {
  product_slug: string;
  window_days: number;
  audience: {
    followers: number;
    followers_prev: number;
    reach: number;
    impressions: number;
    engagements: number;
    engagement_rate: number | null;
  };
  networks: Array<{
    source: "meta_social" | "x_social";
    label: string;
    connected: boolean;
    followers: number;
    impressions: number;
    engagements: number;
    engagement_rate: number | null;
  }>;
  trend: Array<{ day: string; followers: number; impressions: number; engagements: number }>;
  posts: Array<{
    source: "meta_social" | "x_social";
    post_id: string;
    post_url: string | null;
    published_at: string;
    content_text: string | null;
    media_type: string | null;
    impressions: number;
    likes: number;
    comments: number;
    shares: number;
    engagement_rate: number | null;
  }>;
};

export type FunnelStep = { stage: Stage; label: string };

export type FunnelDefinition = {
  steps: FunnelStep[];
  is_default: boolean;
  available: Array<{ stage: Stage; volume_30d: number; events_30d: number; sources: string[] }>;
};

export type FunnelSeriesRow = { day: string; stage: Stage; volume: number };

export type LandingCharacteristicScore = {
  id: string;
  label: string;
  weight: number;
  score: number;
  rationale: string;
};

export type LandingPageAnalysis = {
  id: string;
  analyzed_at: string;
  model: string;
  overall_score: number | null;
  scores: LandingCharacteristicScore[];
  summary: string | null;
  status: "ok" | "error";
  error: string | null;
};

export type DetectedToolCategory =
  | "analytics"
  | "tag_manager"
  | "advertising"
  | "recordings"
  | "funnel"
  | "payments"
  | "crm"
  | "other";

export type DetectedIntegration = {
  key: string;
  name: string;
  category: DetectedToolCategory;
  fk_supported: boolean;
  fk_kind: string | null;
  detected_id: string | null;
  details: string | null;
  recommendation: string | null;
  is_connected?: boolean;
  connection_status?: "active" | "pending" | "error" | "disabled" | "not_connected";
};

export type DetectedPlatform = {
  name: string;
  category: "ecommerce" | "cms" | "framework" | "builder";
  details?: string | null;
};

export type DetectIntegrationsResponse = {
  url: string;
  final_url: string;
  domain: string;
  detected_count: number;
  integrations: DetectedIntegration[];
  platform: DetectedPlatform | null;
  summary: string;
};

export type LandingPageTraffic = {
  sessions: number;
  sessions_prev: number;
  engaged: number;
  engaged_prev: number;
  conversions: number;
  conversions_prev: number;
  engagement_rate: number | null;
  engagement_rate_prev: number | null;
  conversion_rate: number | null;
  conversion_rate_prev: number | null;
};

export type RecordingProvider = "clarity" | "posthog";

export type PageFrictionSignals = {
  provider: RecordingProvider;
  recordings_url: string;
  recordings: number;
  rage_clicks: number;
  dead_clicks: number;
  script_errors: number;
};

export type SessionRecording = {
  provider: RecordingProvider;
  session_id: string;
  user_id: string | null;
  started_at: string | null;
  duration_ms: number | null;
  page_count: number | null;
  click_count: number | null;
  entry_url: string | null;
  entry_path: string | null;
  playback_url: string;
  signals: { rage_click?: boolean; dead_click?: boolean; js_error?: boolean };
};

export type LandingPage = {
  id: string;
  path: string;
  url: string;
  source: "manual" | "ga4" | "clarity" | "posthog";
  created_at: string;
  traffic: LandingPageTraffic;
  latest_analysis: Omit<LandingPageAnalysis, "error"> | null;
  friction: PageFrictionSignals | null;
  is_excluded?: boolean;
};

export type LandingPageFilter = {
  id: string;
  product_id?: string;
  pattern: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
};

export type LandingPageFiltersResponse = {
  filters: LandingPageFilter[];
};

export type LandingPageList = { window_days: number; pages: LandingPage[]; filters?: LandingPageFilter[] };

export type LandingPageDetail = {
  id: string;
  path: string;
  url: string;
  source: "manual" | "ga4" | "clarity" | "posthog";
  created_at: string;
  traffic_series: Array<{ day: string; sessions: number; engaged: number; conversions: number }>;
  analyses: LandingPageAnalysis[];
  recordings: SessionRecording[];
  recordings_url: string | null;
  friction: PageFrictionSignals | null;
};

export type ConversionTier = "primary" | "secondary";
export type ConversionValueMode = "none" | "fixed" | "transaction";
export type ConversionSource = "mysql" | "postgres" | "mongo" | "ga4" | "stripe";

export type ConversionScoreStats = {
  count_scored: number;
  avg_score: number | null;
  grades: Record<string, number> | null;
};

export type ConversionDef = {
  id: string;
  key: string;
  label: string;
  tier: ConversionTier;
  stage: Stage;
  source: ConversionSource | null;
  event_name: string | null;
  value_mode: ConversionValueMode;
  fixed_value_cents: number | null;
  currency: string | null;
  score_mode: ConversionScoreMode;
  target_cpa_cents: number | null;
  grade_weights: Record<string, number> | null;
  is_active: boolean;
  count_30d: number;
  cpa_30d_cents: number | null;
  score_stats_30d: ConversionScoreStats | null;
  value_30d_cents: number | null;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type ConversionSave = {
  key: string;
  label: string;
  tier: ConversionTier;
  stage: Stage;
  source?: ConversionSource | null;
  event_name?: string | null;
  value_mode?: ConversionValueMode;
  fixed_value_cents?: number | null;
  currency?: string | null;
  score_mode?: ConversionScoreMode;
  target_cpa_cents?: number | null;
  grade_weights?: Record<string, number> | null;
  is_active?: boolean;
};

export type ConversionReport = {
  window_days: number;
  conversions: Array<{
    key: string;
    label: string;
    tier: ConversionTier;
    stage: Stage;
    value_mode: ConversionValueMode;
    currency: string | null;
    score_mode: ConversionScoreMode;
    target_cpa_cents: number | null;
    grade_weights: Record<string, number> | null;
    count: number;
    prev_count: number;
    cpa_cents: number | null;
    prev_cpa_cents: number | null;
    score_stats: ConversionScoreStats | null;
    value_cents: number | null;
    prev_value_cents: number | null;
    by_channel: Array<{
      channel: string;
      count: number;
      cpa_cents: number | null;
      score_stats: ConversionScoreStats | null;
      value_cents: number | null;
    }>;
  }>;
};

export type Ga4EventMap = { event: string; stage: Stage };
export type PosthogEventMap = { event: string; stage: Stage };

export type RevenueBasis = "net" | "gross";
export type MoneyColumn = {
  table: string;
  column: string;
  looks_like: "net" | "gross" | "unclear";
  sample: string | null;
};

export type UnresolvedMoneyTable = {
  table: string;
  columns: Array<{ column: string; looks_like: "net" | "gross" | "unclear"; sample: string | null }>;
};

export type MysqlEventMap = {
  table: string;
  pk: string;
  ts_col: string;
  stage: Stage;
  score_col?: string;
  identity?: { email_col?: string; external_id_col?: string };
  attribution?: {
    channel_col?: string;
    utm_source_col?: string;
    utm_medium_col?: string;
    utm_campaign_col?: string;
    gclid_col?: string;
    fbclid_col?: string;
    li_fat_id_col?: string;
    referrer_col?: string;
    campaign_id_col?: string;
    ad_group_id_col?: string;
    ad_id_col?: string;
    target_id_col?: string;
    keyword_col?: string;
    match_type_col?: string;
  };
  revenue?: { amount_col: string; currency: string; kind?: string; is_dollars?: boolean; basis?: RevenueBasis };
};

export type MysqlCredentials = { host: string; port?: number; user: string; password: string; database: string };

export type MappingPresetHit = {
  id: string;
  label: string;
  description: string;
  event_maps: MysqlEventMap[];
};

export type SuggestMappingsResult = {
  source: "preset" | "ai";
  model?: string;
  schema_summary: {
    tables: number;
    sample_rows: boolean;
    table_names?: string[];
    money_columns?: MoneyColumn[];
    unresolved_money_tables?: UnresolvedMoneyTable[];
  };
  suggestions: MysqlEventMap[];
  presets?: MappingPresetHit[];
};

export type PostgresEventMap = {
  schema?: string;
  table: string;
  pk: string;
  ts_col: string;
  stage: Stage;
  score_col?: string;
  identity?: { email_col?: string; external_id_col?: string };
  attribution?: {
    channel_col?: string;
    utm_source_col?: string;
    utm_medium_col?: string;
    utm_campaign_col?: string;
    gclid_col?: string;
    fbclid_col?: string;
    li_fat_id_col?: string;
    referrer_col?: string;
    campaign_id_col?: string;
    ad_group_id_col?: string;
    ad_id_col?: string;
    target_id_col?: string;
    keyword_col?: string;
    match_type_col?: string;
  };
  revenue?: { amount_col: string; currency: string; kind?: string; is_dollars?: boolean; basis?: RevenueBasis };
};

export type PostgresCredentials = { host: string; port?: number; user: string; password: string; database: string; schema?: string };

export type MongoEventMap = {
  collection: string;
  pk_field: string;
  ts_field: string;
  stage: Stage;
  score_field?: string;
  identity?: { email_field?: string; external_id_field?: string };
  attribution?: {
    channel_field?: string;
    utm_source_field?: string;
    utm_medium_field?: string;
    utm_campaign_field?: string;
    gclid_field?: string;
    fbclid_field?: string;
    li_fat_id_field?: string;
    referrer_field?: string;
    campaign_id_field?: string;
    ad_group_id_field?: string;
    ad_id_field?: string;
    target_id_field?: string;
    keyword_field?: string;
    match_type_field?: string;
  };
  revenue?: { amount_field: string; currency: string; kind?: string; is_dollars?: boolean; basis?: RevenueBasis };
};

export type MongoCredentials = { uri: string };

export type HealthCheck = {
  id: string;
  label: string;
  status: "pass" | "warn" | "fail" | "skip";
  evidence: string;
  action: string | null;
};

export type HealthReport = {
  product_slug: string;
  generated_at: string;
  window_days: number;
  score: number | null;
  grade: string | null;
  categories: Array<{
    key: "tracking" | "funnel" | "advertising" | "seo_social";
    label: string;
    score: number | null;
    checks: HealthCheck[];
  }>;
};

export type GrowthCategory = "activation" | "friction" | "acquisition" | "pricing" | "seo";
export type GrowthDifficulty = "quick_fix" | "feature_build" | "experiment";
export type GrowthTool = "cursor" | "claude_code" | "lovable" | "bolt" | "v0";

export type GrowthAction = {
  id: string;
  headline: string;
  metric_line: string;
  category: GrowthCategory;
  difficulty: GrowthDifficulty;
  revenue_impact_cents: number | null;
  currency: string;
  evidence: string;
  instruction: string;
  done_when: string;
  context: {
    from_stage?: string;
    to_stage?: string;
    dropoff_rate?: number;
    channel?: string;
    path?: string;
    playback_urls?: string[];
    target_files?: string[];
  };
  prompt: string;
};

export type GrowthReport = {
  product_slug: string;
  product_name: string;
  generated_at: string;
  window_days: number;
  actions: GrowthAction[];
};

export type GrowthPrompt = {
  id: string;
  tool: GrowthTool;
  prompt: string;
  headline: string;
  revenue_impact_cents: number | null;
  currency: string;
};

export type TeamMember = {
  id: string;
  email: string;
  name: string | null;
  member_role: "owner" | "member";
  status: "active" | "invited" | "disabled";
  created_at: string;
  last_seen_at: string | null;
};

export type HistoryEvent = {
  id: number;
  occurred_at: string;
  actor: string;
  via: string;
  action: string;
  product_slug: string | null;
  subject_ref: string | null;
  card_headline: string | null;
  card_type: string | null;
  payload: Record<string, unknown>;
};

export type ChatMessage = { role: "user" | "assistant"; content: string };

export type ChatSuggestedAction = {
  kind: "navigate" | "copy" | "approve" | "reject";
  label: string;
  href?: string;
  text?: string;
  card_id?: string;
  tool?: GrowthTool;
};

export type ChatToolCall = {
  name: string;
  status: "running" | "done" | "error";
  label: string;
  error?: string;
};

export type ChatResponse = {
  reply: string;
  model: string;
  tool_calls: ChatToolCall[];
  suggested_actions: ChatSuggestedAction[];
};

export type ChatStreamEvent =
  | { type: "tool"; name: string; status: ChatToolCall["status"]; label: string; error?: string }
  | { type: "delta"; text: string }
  | { type: "done"; reply: string; model: string; tool_calls: ChatToolCall[]; suggested_actions: ChatSuggestedAction[] }
  | { type: "error"; error: string };

export class ApiError extends Error {
  readonly code?: string;
  readonly limit?: string;
  readonly upgrade_to?: string;
  constructor(
    message: string,
    readonly status: number,
    extra?: { code?: string; limit?: string; upgrade_to?: string },
  ) {
    super(message);
    this.code = extra?.code;
    this.limit = extra?.limit;
    this.upgrade_to = extra?.upgrade_to;
  }
}

/**
 * `quiet` keeps a request out of the in-flight count. Reserved for background
 * polling: the sync watcher asks every few seconds, and counting those would
 * leave the topbar's progress bar flickering permanently, which reads as the
 * page never finishing loading — the opposite of what the watcher is for.
 */
async function req<T>(path: string, init?: RequestInit, quiet = false): Promise<T> {
  let res: Response;
  const session = getSession();
  if (!quiet) beginRequest();
  const endRequest = () => { if (!quiet) endRequestCounted(); };
  try {
    const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) };
    if (init?.body !== undefined) headers["content-type"] = "application/json";
    if (session) headers["authorization"] = `Bearer ${session.token}`;
    res = await fetch(`${BASE}${path}`, { ...init, headers });
  } catch {
    endRequest();
    throw new ApiError(`Can't reach the API at ${BASE}.`, 0);
  }
  endRequest();
  // A 401 outside the auth flow means the session died: back to login, with a
  // return-to so the user lands where they were afterwards.
  if (res.status === 401 && !path.startsWith("/auth/")) {
    sessionStorage.setItem("fk_return_to", location.hash);
    clearSession();
    go({ name: "login" });
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let msg = `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(body) as { error?: string; code?: string; limit?: string; upgrade_to?: string };
      if (parsed.error) msg = parsed.error;
      if (res.status === 402) msg = `${msg} Open Billing to upgrade.`;
      throw new ApiError(msg, res.status, parsed);
    } catch (e) {
      if (e instanceof ApiError) throw e;
      /* body wasn't JSON; keep the status line */
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export const api = {
  portfolio: () => req<ProductRow[]>("/portfolio"),
  product: (slug: string) => req<ProductRow>(`/products/${encodeURIComponent(slug)}`),
  queue: () => req<QueueCard[]>("/queue"),
  funnel: (slug: string, days: number) =>
    req<FunnelRow[]>(`/products/${encodeURIComponent(slug)}/funnel?days=${days}`),
  payback: (slug: string) => req<Payback>(`/products/${encodeURIComponent(slug)}/payback`),
  health: () => req<Health>("/health"),
  connections: () => req<Connection[]>("/connections"),
  /** The same inventory, fetched without touching the topbar progress bar. */
  connectionsQuiet: () => req<Connection[]>("/connections", undefined, true),

  attribution: (slug: string, days: number) =>
    req<AttributionReport>(`/products/${encodeURIComponent(slug)}/attribution?days=${days}`),
  attributionSales: (slug: string, days: number, opts: { channel?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams({ days: String(days) });
    if (opts.channel) qs.set("channel", opts.channel);
    if (opts.limit) qs.set("limit", String(opts.limit));
    return req<AttributionSales>(`/products/${encodeURIComponent(slug)}/attribution/sales?${qs}`);
  },
  attributionAds: (slug: string, days: number, level: AdLevel, limit = 100) =>
    req<AdPerformanceReport>(
      `/products/${encodeURIComponent(slug)}/attribution/ads?days=${days}&level=${level}&limit=${limit}`),
  adsUrlSuffix: {
    get: (slug: string) =>
      req<AdsUrlSuffixStatus>(`/products/${encodeURIComponent(slug)}/connections/google-ads/url-suffix`),
    deploy: (slug: string) =>
      req<{ ok: true; suffix: string; added: string[]; already_complete: boolean }>(
        `/products/${encodeURIComponent(slug)}/connections/google-ads/url-suffix`, { method: "POST" }),
  },
  timeseries: (slug: string, days: number) =>
    req<TimeseriesRow[]>(`/products/${encodeURIComponent(slug)}/timeseries?days=${days}`),
  demand: (slug: string, days = 30) =>
    req<DemandReport>(`/products/${encodeURIComponent(slug)}/demand?days=${days}`),
  social: (slug: string, days = 30) =>
    req<SocialReport>(`/products/${encodeURIComponent(slug)}/social?days=${days}`),
  healthReport: (slug: string, days = 30) =>
    req<HealthReport>(`/products/${encodeURIComponent(slug)}/health-report?days=${days}`),
  scorecard: {
    get: (slug: string, weeks = 12, current = false) =>
      req<ScorecardView>(`/products/${encodeURIComponent(slug)}/scorecard?weeks=${weeks}${current ? "&current=1" : ""}`),
    insights: (slug: string, weeks = 12) =>
      req<ScorecardAnalysis>(`/products/${encodeURIComponent(slug)}/scorecard/insights?weeks=${weeks}`),
    save: (slug: string, body: { sections: Array<{ id: string; label: string; metrics: ScorecardMetric[] }>; template?: string | null }) =>
      req<ScorecardView>(`/products/${encodeURIComponent(slug)}/scorecard`, {
        method: "POST",
        body: JSON.stringify({
          sections: body.sections.map((s) => ({
            id: s.id, label: s.label,
            metrics: s.metrics.map((m) => ({
              key: m.key, label: m.label, kind: m.kind,
              metric_ref: m.metric_ref, formula: m.formula, format: m.format, target: m.target,
            })),
          })),
          template: body.template,
        }),
      }),
    values: (slug: string, cells: Array<{ metric_key: string; week_start: string; value: number | null }>) =>
      req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/scorecard/values`, {
        method: "POST", body: JSON.stringify({ cells }),
      }),
    suggest: (slug: string, prompt?: string) =>
      req<ScorecardSuggest>(`/products/${encodeURIComponent(slug)}/scorecard/suggest`, {
        method: "POST", body: JSON.stringify({ prompt }),
      }),
    template: (slug: string, template: "default" | "saas_waitlist" | "paid_acquisition" | "content_led") =>
      req<ScorecardView>(`/products/${encodeURIComponent(slug)}/scorecard/template`, {
        method: "POST", body: JSON.stringify({ template }),
      }),
    share: (slug: string) =>
      req<{ token: string; url: string }>(`/products/${encodeURIComponent(slug)}/scorecard/share`, {
        method: "POST", body: "{}",
      }),
    revokeShare: (slug: string) =>
      req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/scorecard/share/revoke`, {
        method: "POST", body: "{}",
      }),
    sheetsSync: (slug: string, weeks = 12) =>
      req<{ spreadsheet_id: string; url: string }>(
        `/products/${encodeURIComponent(slug)}/scorecard/sheets/sync?weeks=${weeks}`,
        { method: "POST", body: "{}" }),
    download: async (slug: string, kind: "csv" | "xlsx", weeks = 12, current = false) => {
      const session = getSession();
      const path = `/products/${encodeURIComponent(slug)}/scorecard/export.${kind}?weeks=${weeks}${current ? "&current=1" : ""}`;
      const res = await fetch(`${BASE}${path}`, {
        headers: session ? { authorization: `Bearer ${session.token}` } : {},
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        let msg = `${res.status} ${res.statusText}`;
        try {
          const parsed = JSON.parse(body) as { error?: string };
          if (parsed.error) msg = parsed.error;
        } catch { /* */ }
        if (res.status === 402) msg = `${msg} Open Billing to upgrade.`;
        throw new ApiError(msg, res.status);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${slug}-scorecard.${kind}`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
    public: (token: string) =>
      fetch(`${BASE}/t/scorecard/${encodeURIComponent(token)}`).then(async (res) => {
        if (!res.ok) throw new ApiError("This share link is no longer valid.", res.status);
        return res.json() as Promise<ScorecardView>;
      }),
  },
  growthActions: (slug: string, days = 30, tool: GrowthTool = "cursor") =>
    req<GrowthReport>(
      `/products/${encodeURIComponent(slug)}/growth-actions?days=${days}&tool=${tool}`),
  growthPrompt: (slug: string, id: string, tool: GrowthTool = "cursor", days = 30) =>
    req<GrowthPrompt>(
      `/products/${encodeURIComponent(slug)}/growth-actions/${encodeURIComponent(id)}/prompt`,
      { method: "POST", body: JSON.stringify({ tool, days }) }),
  landingPages: {
    list: (slug: string, days = 30) =>
      req<LandingPageList>(`/products/${encodeURIComponent(slug)}/landing-pages?days=${days}`),
    add: (slug: string, url: string) =>
      req<LandingPage>(`/products/${encodeURIComponent(slug)}/landing-pages`, {
        method: "POST", body: JSON.stringify({ url }),
      }),
    get: (slug: string, id: string, days = 30) =>
      req<LandingPageDetail>(
        `/products/${encodeURIComponent(slug)}/landing-pages/${encodeURIComponent(id)}?days=${days}`),
    analyze: (slug: string, id: string) =>
      req<LandingPageAnalysis>(
        `/products/${encodeURIComponent(slug)}/landing-pages/${encodeURIComponent(id)}/analyze`,
        { method: "POST", body: "{}" }),
    remove: (slug: string, id: string) =>
      req<{ ok: true }>(
        `/products/${encodeURIComponent(slug)}/landing-pages/${encodeURIComponent(id)}`,
        { method: "DELETE" }),
    filters: {
      list: (slug: string) =>
        req<LandingPageFiltersResponse>(
          `/products/${encodeURIComponent(slug)}/landing-pages/filters`),
      add: (slug: string, pattern: string, enabled = true) =>
        req<LandingPageFilter>(
          `/products/${encodeURIComponent(slug)}/landing-pages/filters`,
          { method: "POST", body: JSON.stringify({ pattern, enabled }) }),
      update: (slug: string, id: string, patch: { pattern?: string; enabled?: boolean }) =>
        req<LandingPageFilter>(
          `/products/${encodeURIComponent(slug)}/landing-pages/filters/${encodeURIComponent(id)}`,
          { method: "POST", body: JSON.stringify(patch) }),
      remove: (slug: string, id: string) =>
        req<{ ok: true }>(
          `/products/${encodeURIComponent(slug)}/landing-pages/filters/${encodeURIComponent(id)}`,
          { method: "DELETE" }),
    },
  },
  history: (opts: { product?: string; days?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.product) qs.set("product", opts.product);
    if (opts.days) qs.set("days", String(opts.days));
    if (opts.limit) qs.set("limit", String(opts.limit));
    const s = qs.toString();
    return req<HistoryEvent[]>(`/history${s ? `?${s}` : ""}`);
  },

  funnelDef: {
    get: (slug: string) =>
      req<FunnelDefinition>(`/products/${encodeURIComponent(slug)}/funnel/definition`),
    save: (slug: string, steps: FunnelStep[]) =>
      req<{ ok: true; steps: FunnelStep[] }>(`/products/${encodeURIComponent(slug)}/funnel/definition`, {
        method: "POST", body: JSON.stringify({ steps }),
      }),
    series: (slug: string, days: number) =>
      req<FunnelSeriesRow[]>(`/products/${encodeURIComponent(slug)}/funnel/series?days=${days}`),
  },

  conversions: {
    list: (slug: string) =>
      req<{ conversions: ConversionDef[] }>(`/products/${encodeURIComponent(slug)}/conversions`),
    save: (slug: string, def: ConversionSave) =>
      req<{ ok: true; conversion: ConversionDef }>(`/products/${encodeURIComponent(slug)}/conversions`, {
        method: "POST", body: JSON.stringify(def),
      }),
    remove: (slug: string, key: string) =>
      req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/conversions/${encodeURIComponent(key)}/delete`, {
        method: "POST", body: "{}",
      }),
    report: (slug: string, days: number) =>
      req<ConversionReport>(`/products/${encodeURIComponent(slug)}/conversions/report?days=${days}`),
  },

  setGa4EventMaps: (slug: string, event_maps: Ga4EventMap[]) =>
    req<{ ok: true; event_maps: Ga4EventMap[] }>(
      `/products/${encodeURIComponent(slug)}/connections/ga4/event-maps`, {
        method: "POST", body: JSON.stringify({ event_maps }),
      }),

  setPosthogEventMaps: (slug: string, event_maps: PosthogEventMap[]) =>
    req<{ ok: true; event_maps: PosthogEventMap[] }>(
      `/products/${encodeURIComponent(slug)}/connections/posthog/event-maps`, {
        method: "POST", body: JSON.stringify({ event_maps }),
      }),

  // Tenant MySQL connect: credentials go to the encrypted credential store,
  // never into readable config.
  connectMysql: (slug: string, body: { credentials: MysqlCredentials; event_maps?: MysqlEventMap[]; status?: "active" | "pending" }) =>
    req<{ ok: true; connection_id: string; status: string }>(
      `/products/${encodeURIComponent(slug)}/connections/mysql`, {
        method: "POST", body: JSON.stringify(body),
      }),
  setMysqlEventMaps: (slug: string, event_maps: MysqlEventMap[]) =>
    req<{ ok: true; event_maps: MysqlEventMap[] }>(
      `/products/${encodeURIComponent(slug)}/connections/mysql/event-maps`, {
        method: "POST", body: JSON.stringify({ event_maps }),
      }),
  suggestMysqlMappings: (slug: string, body: { source: "preset" | "ai"; preset_id?: string; tables?: string[] }) =>
    req<SuggestMappingsResult>(
      `/products/${encodeURIComponent(slug)}/connections/mysql/suggest-mappings`, {
        method: "POST", body: JSON.stringify(body),
      }),

  // Tenant Postgres connect: credentials go to the encrypted credential store,
  // never into readable config. Tenant-only (no operator env-ref path).
  connectPostgres: (slug: string, body: { credentials: PostgresCredentials; event_maps?: PostgresEventMap[]; status?: "active" | "pending" }) =>
    req<{ ok: true; connection_id: string; status: string }>(
      `/products/${encodeURIComponent(slug)}/connections/postgres`, {
        method: "POST", body: JSON.stringify(body),
      }),
  setPostgresEventMaps: (slug: string, event_maps: PostgresEventMap[]) =>
    req<{ ok: true; event_maps: PostgresEventMap[] }>(
      `/products/${encodeURIComponent(slug)}/connections/postgres/event-maps`, {
        method: "POST", body: JSON.stringify({ event_maps }),
      }),

  // Tenant MongoDB connect: a single connection string, encrypted at rest.
  connectMongo: (slug: string, body: { credentials: MongoCredentials; event_maps?: MongoEventMap[]; status?: "active" | "pending" }) =>
    req<{ ok: true; connection_id: string; status: string }>(
      `/products/${encodeURIComponent(slug)}/connections/mongo`, {
        method: "POST", body: JSON.stringify(body),
      }),
  setMongoEventMaps: (slug: string, event_maps: MongoEventMap[]) =>
    req<{ ok: true; event_maps: MongoEventMap[] }>(
      `/products/${encodeURIComponent(slug)}/connections/mongo/event-maps`, {
        method: "POST", body: JSON.stringify({ event_maps }),
      }),

  dashboards: {
    list: (slug: string) => req<Dashboard[]>(`/products/${encodeURIComponent(slug)}/dashboards`),
    create: (slug: string, body: { name: string; description?: string; spec?: DashboardSpec; prompt?: string }) =>
      req<Dashboard>(`/products/${encodeURIComponent(slug)}/dashboards`, {
        method: "POST", body: JSON.stringify(body),
      }),
    generate: (slug: string, prompt: string) =>
      req<GeneratedDashboard>(`/products/${encodeURIComponent(slug)}/dashboards/generate`, {
        method: "POST", body: JSON.stringify({ prompt }),
      }),
    update: (id: string, body: { name?: string; description?: string | null; spec?: DashboardSpec }) =>
      req<Dashboard>(`/dashboards/${encodeURIComponent(id)}`, { method: "POST", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/dashboards/${encodeURIComponent(id)}/delete`, { method: "POST", body: "{}" }),
  },

  chat: {
    send: (opts: { slug?: string | null; messages: ChatMessage[]; section?: string }) =>
      req<ChatResponse>(
        opts.slug ? `/products/${encodeURIComponent(opts.slug)}/chat` : "/chat",
        { method: "POST", body: JSON.stringify({ messages: opts.messages, section: opts.section }) },
      ),
    stream: async (
      opts: { slug?: string | null; messages: ChatMessage[]; section?: string },
      handlers: {
        onTool?: (e: Extract<ChatStreamEvent, { type: "tool" }>) => void;
        onDelta?: (text: string) => void;
        onDone?: (res: ChatResponse) => void;
        onError?: (error: string) => void;
      },
    ): Promise<ChatResponse> => {
      const session = getSession();
      const path = opts.slug ? `/products/${encodeURIComponent(opts.slug)}/chat` : "/chat";
      beginRequest();
      let res: Response;
      try {
        res = await fetch(`${BASE}${path}`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            accept: "text/event-stream",
            ...(session ? { authorization: `Bearer ${session.token}` } : {}),
          },
          body: JSON.stringify({ messages: opts.messages, stream: true, section: opts.section }),
        });
      } catch {
        endRequestCounted();
        throw new ApiError(`Can't reach the API at ${BASE}.`, 0);
      }
      if (res.status === 401) {
        endRequestCounted();
        sessionStorage.setItem("fk_return_to", location.hash);
        clearSession();
        go({ name: "login" });
        throw new ApiError("Session expired.", 401);
      }
      const ctype = res.headers.get("content-type") ?? "";
      if (!res.ok || !ctype.includes("text/event-stream")) {
        endRequestCounted();
        const raw = await res.text().catch(() => "");
        let msg = `${res.status} ${res.statusText}`;
        try {
          const parsed = JSON.parse(raw) as { error?: string };
          if (parsed.error) msg = parsed.error;
        } catch { /* keep */ }
        if (res.status === 402) msg = `${msg} Open Billing to upgrade.`;
        throw new ApiError(msg, res.status);
      }
      const reader = res.body?.getReader();
      if (!reader) {
        endRequestCounted();
        throw new ApiError("The chat stream did not start.", res.status);
      }
      const decoder = new TextDecoder();
      let buf = "";
      let final: ChatResponse | null = null;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const chunks = buf.split("\n\n");
          buf = chunks.pop() ?? "";
          for (const chunk of chunks) {
            const line = chunk.split("\n").find((l) => l.startsWith("data: "));
            if (!line) continue;
            let event: ChatStreamEvent;
            try { event = JSON.parse(line.slice(6)) as ChatStreamEvent; }
            catch { continue; }
            if (event.type === "tool") handlers.onTool?.(event);
            else if (event.type === "delta") handlers.onDelta?.(event.text);
            else if (event.type === "done") {
              final = {
                reply: event.reply,
                model: event.model,
                tool_calls: event.tool_calls,
                suggested_actions: event.suggested_actions,
              };
              handlers.onDone?.(final);
            } else if (event.type === "error") {
              handlers.onError?.(event.error);
              throw new ApiError(event.error, 502);
            }
          }
        }
      } finally {
        endRequestCounted();
      }
      if (!final) throw new ApiError("The Keeper closed the stream without a reply.", 502);
      return final;
    },
  },

  // The server derives the actor from the authenticated session; no client label.
  approve: (id: string) => req<{ ok: true }>(`/queue/${id}/approve`, { method: "POST", body: "{}" }),
  reject: (id: string) => req<{ ok: true }>(`/queue/${id}/reject`, { method: "POST", body: "{}" }),
  restore: (id: string) => req<{ ok: true }>(`/queue/${id}/restore`, { method: "POST", body: "{}" }),
  snooze: (id: string, snoozeHours = 24) =>
    req<{ ok: true }>(`/queue/${id}/snooze`, { method: "POST", body: JSON.stringify({ snoozeHours }) }),

  auth: {
    signup: (
      email: string,
      password: string,
      anonId?: string,
      meta?: {
        event_id: string;
        fbp?: string;
        fbc?: string;
        source_url: string;
      },
    ) =>
      req<{ ok: true }>("/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          ...(anonId ? { anonId } : {}),
          ...(meta ? { meta } : {}),
        }),
      }),
    login: (email: string, password: string) =>
      req<{ token: string; expires_at: string; account: Session["account"] }>(
        "/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
    googleComplete: (token: string) =>
      req<{ token: string; expires_at: string; account: Session["account"]; is_new?: boolean }>(
        "/auth/google/complete", { method: "POST", body: JSON.stringify({ token }) }),
    logout: () => req<{ ok: true }>("/auth/logout", { method: "POST" }),
    me: () => req<Session["account"] & { verified: boolean }>("/auth/me"),
    updateMe: (body: { name?: string | null; current_password?: string; new_password?: string }) =>
      req<Session["account"] & { verified: boolean }>("/auth/me", { method: "POST", body: JSON.stringify(body) }),
    accounts: () => req<MembershipSummary[]>("/auth/accounts"),
    switchAccount: (account_id: string) =>
      req<{ account: Session["account"]; accounts: MembershipSummary[] }>(
        "/auth/switch-account", { method: "POST", body: JSON.stringify({ account_id }) }),
    createAccount: (name: string) =>
      req<{ account: Session["account"]; accounts: MembershipSummary[] }>(
        "/auth/accounts", { method: "POST", body: JSON.stringify({ name }) }),
    invitePeek: (token: string) =>
      req<{ email: string; account_name: string; needs_password: boolean }>(
        `/auth/invite?token=${encodeURIComponent(token)}`),
    acceptInvite: (token: string, password?: string, name?: string) =>
      req<{ token: string; expires_at: string; account: Session["account"] }>(
        "/auth/accept-invite", { method: "POST", body: JSON.stringify({ token, password, name }) }),
    resetConfirm: (token: string, password: string) =>
      req<{ ok: true }>("/auth/password-reset/confirm", {
        method: "POST", body: JSON.stringify({ token, password }),
      }),
    keys: () => req<Array<{ id: string; name: string; prefix: string; created_at: string; last_used_at: string | null }>>("/auth/keys"),
    createKey: (name: string) =>
      req<{ id: string; name: string; key: string }>("/auth/keys", { method: "POST", body: JSON.stringify({ name }) }),
    revokeKey: (id: string) =>
      req<{ ok: true }>(`/auth/keys/${encodeURIComponent(id)}/revoke`, { method: "POST", body: "{}" }),
  },

  team: {
    list: () => req<TeamMember[]>("/team"),
    invite: (email: string, member_role: "owner" | "member") =>
      req<{ ok: true; user_id: string; invite_url: string; expires_in_hours: number; already_registered: boolean }>(
        "/team/invites", { method: "POST", body: JSON.stringify({ email, member_role }) }),
    disable: (id: string) => req<{ ok: true }>(`/team/${id}/disable`, { method: "POST", body: "{}" }),
    enable: (id: string) => req<{ ok: true }>(`/team/${id}/enable`, { method: "POST", body: "{}" }),
    setRole: (id: string, member_role: "owner" | "member") =>
      req<{ ok: true }>(`/team/${id}/role`, { method: "POST", body: JSON.stringify({ member_role }) }),
  },

  createProduct: (p: { name: string; slug: string; currency?: string; domain?: string }) =>
    req<{ id: string; slug: string; name: string }>("/products", { method: "POST", body: JSON.stringify(p) }),

  updateProduct: (slug: string, p: { name?: string; currency?: string; domain?: string | null }) =>
    req<{ id: string; slug: string; name: string; currency: string; domain: string | null }>(
      `/products/${encodeURIComponent(slug)}`, { method: "POST", body: JSON.stringify(p) }),

  connect: {
    start: (product_slug: string, kinds: string[]) =>
      req<{ state: string; auth_url: string; expires_at: string }>(
        "/connect/google/start", { method: "POST", body: JSON.stringify({ product_slug, kinds, client: "dashboard" }) }),
    status: (state: string) =>
      req<ConnectStatus>(`/connect/google/status?state=${encodeURIComponent(state)}`),
    select: (body: { state: string; ga4_property_id?: string; gtm_container_path?: string; ads_customer_id?: string; gsc_site_url?: string }) =>
      req<{ ok: true; activated: string[] }>("/connect/google/select", { method: "POST", body: JSON.stringify(body) }),
    saStart: (product_slug: string, kinds: Array<"ga4" | "gtm" | "gsc">) =>
      req<{
        sa_email: string;
        kinds: Array<"ga4" | "gtm" | "gsc">;
        instructions: Record<string, { human: string }>;
      }>("/connect/google/service-account/start", {
        method: "POST", body: JSON.stringify({ product_slug, kinds }),
      }),
    saVerify: (body: { product_slug: string; ga4_property_id?: string; gtm_container_path?: string; gsc_site_url?: string }) =>
      req<{
        ok?: true;
        status?: "complete";
        sa_email: string;
        activated?: string[];
        options?: {
          ga4_properties?: Array<{ id: string; name: string }>;
          gtm_containers?: Array<{ path: string; name: string; public_id: string }>;
          gsc_sites?: Array<{ site_url: string }>;
        };
      }>("/connect/google/service-account/verify", { method: "POST", body: JSON.stringify(body) }),
  },

  connectMeta: {
    start: (product_slug: string, kinds: string[] = ["meta_ads"]) =>
      req<{ state: string; auth_url: string; expires_at: string }>(
        "/connect/meta/start", { method: "POST", body: JSON.stringify({ product_slug, kinds, client: "dashboard" }) }),
    status: (state: string) =>
      req<ConnectStatus>(`/connect/meta/status?state=${encodeURIComponent(state)}`),
    select: (body: { state: string; ad_account_id?: string; page_id?: string }) =>
      req<{ ok: true; activated: string[] }>("/connect/meta/select", { method: "POST", body: JSON.stringify(body) }),
  },

  setSemrushKey: (slug: string, api_key: string, domain: string) =>
    req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/connections/semrush`, {
      method: "POST", body: JSON.stringify({ api_key, domain }),
    }),
  connectClarity: (slug: string, api_token: string, project_id: string) =>
    req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/connections/clarity`, {
      method: "POST", body: JSON.stringify({ api_token, project_id }),
    }),
  connectPosthog: (slug: string, api_key: string, project_id: string, host?: string) =>
    req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/connections/posthog`, {
      method: "POST", body: JSON.stringify({ api_key, project_id, host }),
    }),
  connectOpinly: (slug: string, api_key: string, company_id?: string) =>
    req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/connections/opinly`, {
      method: "POST", body: JSON.stringify({ api_key, company_id: company_id || undefined }),
    }),
  connectBing: (slug: string, api_key: string, site_url: string) =>
    req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/connections/bing`, {
      method: "POST", body: JSON.stringify({ api_key, site_url }),
    }),
  connectMetaAds: (slug: string, access_token: string, ad_account_id?: string) =>
    req<{ ok: true; connection_id: string | null; accounts?: Array<{ id: string; name: string; currency: string }> }>(
      `/products/${encodeURIComponent(slug)}/connections/meta-ads`, {
        method: "POST", body: JSON.stringify({ access_token, ad_account_id }),
      }),
  setMetaAdsCampaignFilter: (slug: string, campaign_name_prefixes: string[]) =>
    req<{ ok: true; campaign_name_prefixes: string[] }>(
      `/products/${encodeURIComponent(slug)}/connections/meta-ads/campaign-filter`, {
        method: "POST", body: JSON.stringify({ campaign_name_prefixes }),
      }),
  connectLinkedinAds: (slug: string, access_token: string, ad_account_id?: string) =>
    req<{ ok: true; connection_id: string | null; accounts?: Array<{ id: string; name: string; currency: string }> }>(
      `/products/${encodeURIComponent(slug)}/connections/linkedin-ads`, {
        method: "POST", body: JSON.stringify({ access_token, ad_account_id }),
      }),
  connectMetaSocial: (slug: string, access_token: string, page_id?: string) =>
    req<{ ok: true; connection_id: string | null; pages?: Array<{ id: string; name: string; instagram_id: string | null; instagram_name: string | null }> }>(
      `/products/${encodeURIComponent(slug)}/connections/meta-social`, {
        method: "POST", body: JSON.stringify({ access_token, page_id }),
      }),
  connectXSocial: (slug: string, bearer_token: string, username: string) =>
    req<{ ok: true; connection_id: string }>(
      `/products/${encodeURIComponent(slug)}/connections/x-social`, {
        method: "POST", body: JSON.stringify({ bearer_token, username }),
      }),
  connectStripe: (slug: string, api_key: string, default_stage?: "converted" | "payment") =>
    req<{ ok: true; connection_id: string }>(
      `/products/${encodeURIComponent(slug)}/connections/stripe`, {
        method: "POST", body: JSON.stringify({ api_key, default_stage }),
      }),
  connectAhrefs: (slug: string, api_key: string, domain: string) =>
    req<{ ok: true }>(`/products/${encodeURIComponent(slug)}/connections/ahrefs`, {
      method: "POST", body: JSON.stringify({ api_key, domain }),
    }),
  testConnection: (slug: string, kind: string) =>
    req<{ ok: boolean; detail: string }>(`/products/${encodeURIComponent(slug)}/connections/${kind}/test`, {
      method: "POST",
    }),
  // Runs the source now rather than at the next sweep, and clears any backoff.
  // Returns when the run STARTS — the connection row reports progress.
  syncConnection: (slug: string, kind: string) =>
    req<{ ok: true; started: boolean; days: number; detail: string }>(
      `/products/${encodeURIComponent(slug)}/connections/${kind}/sync`, { method: "POST" }),

  disconnectConnection: (slug: string, kind: string) =>
    req<{ ok: true; credential_deleted: boolean; revoked_upstream: boolean }>(
      `/products/${encodeURIComponent(slug)}/connections/${kind}/disconnect`, { method: "POST" }),

  trackingKey: (slug: string) =>
    req<TrackingKey>(`/products/${encodeURIComponent(slug)}/tracking-key`),
  rotateTrackingKey: (slug: string) =>
    req<{ ok: true; key: string; created_at: string; rotated: boolean; snippet: string }>(
      `/products/${encodeURIComponent(slug)}/tracking-key`, { method: "POST" }),
  completeOnboarding: (slug: string) =>
    req<{ ok: true; first_time: boolean }>(
      `/products/${encodeURIComponent(slug)}/onboarding-complete`, { method: "POST" }),
  deployTrackingGtm: (slug: string) =>
    req<{ ok: true; workspace_name: string; version_name: string; published: boolean }>(
      `/products/${encodeURIComponent(slug)}/tracking/gtm-deploy`, { method: "POST" }),

  detectIntegrations: (slugOrOpts?: string | { slug?: string; url?: string }, maybeUrl?: string) => {
    let slug: string | undefined;
    let url: string | undefined;
    if (typeof slugOrOpts === "string") {
      slug = slugOrOpts;
      url = maybeUrl;
    } else if (slugOrOpts) {
      slug = slugOrOpts.slug;
      url = slugOrOpts.url;
    }
    const path = slug ? `/products/${encodeURIComponent(slug)}/detect-integrations` : `/tools/detect-integrations`;
    const body = url ? JSON.stringify({ url }) : undefined;
    return req<DetectIntegrationsResponse>(path, { method: "POST", body });
  },

  billing: {
    get: () => req<Billing>("/billing"),
    checkout: (plan: "indie" | "growth") =>
      req<{ url: string }>("/billing/checkout", { method: "POST", body: JSON.stringify({ plan }) }),
    portal: () => req<{ url: string }>("/billing/portal", { method: "POST", body: "{}" }),
  },

  admin: {
    overview: () => req<AdminOverview>("/admin/overview"),
    accounts: (opts: AdminAccountListOpts = {}) => {
      const qs = new URLSearchParams();
      if (opts.q) qs.set("q", opts.q);
      if (opts.plan) qs.set("plan", opts.plan);
      if (opts.status) qs.set("status", opts.status);
      if (opts.sort) qs.set("sort", opts.sort);
      if (opts.dir) qs.set("dir", opts.dir);
      if (opts.limit) qs.set("limit", String(opts.limit));
      if (opts.offset) qs.set("offset", String(opts.offset));
      const suffix = qs.toString() ? `?${qs}` : "";
      return req<AdminAccountList>(`/admin/accounts${suffix}`);
    },
    account: (id: string) => req<AdminAccountDetail>(`/admin/accounts/${encodeURIComponent(id)}`),
    users: (opts: { q?: string; staff?: boolean; limit?: number; offset?: number } = {}) => {
      const qs = new URLSearchParams();
      if (opts.q) qs.set("q", opts.q);
      if (opts.staff) qs.set("staff", "1");
      if (opts.limit) qs.set("limit", String(opts.limit));
      if (opts.offset) qs.set("offset", String(opts.offset));
      const suffix = qs.toString() ? `?${qs}` : "";
      return req<AdminUserList>(`/admin/users${suffix}`);
    },
    verifyLink: (id: string) =>
      req<{ url: string; expires_in_hours: number }>(
        `/admin/users/${encodeURIComponent(id)}/verify-link`, { method: "POST", body: "{}" }),
    resetLink: (id: string) =>
      req<{ url: string; expires_in_hours: number }>(
        `/admin/users/${encodeURIComponent(id)}/reset-link`, { method: "POST", body: "{}" }),
    disableUser: (id: string) =>
      req<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}/disable`, { method: "POST", body: "{}" }),
    enableUser: (id: string) =>
      req<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}/enable`, { method: "POST", body: "{}" }),
    setPlatformAdmin: (id: string, admin: boolean | null) =>
      req<{ ok: true; admin: boolean; source: AdminSource | null }>(
        `/admin/users/${encodeURIComponent(id)}/platform-admin`,
        { method: "POST", body: JSON.stringify({ admin }) }),
    disableAccount: (id: string) =>
      req<{ ok: true }>(`/admin/accounts/${encodeURIComponent(id)}/disable`, { method: "POST", body: "{}" }),
    enableAccount: (id: string) =>
      req<{ ok: true }>(`/admin/accounts/${encodeURIComponent(id)}/enable`, { method: "POST", body: "{}" }),
    setPlan: (id: string, body: { plan: AdminPlan; plan_status: AdminPlanStatus; trial_ends_at?: string | null }) =>
      req<{ ok: true }>(`/admin/accounts/${encodeURIComponent(id)}/plan`, {
        method: "POST", body: JSON.stringify(body),
      }),
    extendProductTrial: (id: string, body: { days?: number; trial_ends_at?: string }) =>
      req<{ ok: true; trial_ends_at: string }>(
        `/admin/products/${encodeURIComponent(id)}/trial`,
        { method: "POST", body: JSON.stringify(body) }),
    impersonate: (id: string) =>
      req<{ token: string; expires_at: string; account: Session["account"] }>(
        `/admin/accounts/${encodeURIComponent(id)}/impersonate`, { method: "POST", body: "{}" }),
    endImpersonation: () =>
      req<{ ok: true }>("/admin/impersonate/end", { method: "POST", body: "{}" }),
  },
};

export type AdminPlan = "free" | "indie" | "growth";
export type AdminPlanStatus = "active" | "trialing" | "past_due" | "canceled";
export type AdminSource = "domain" | "granted" | "revoked";

export type AdminOverview = {
  totals: {
    accounts: number; users: number; products: number;
    connections_active: number; connections_error: number; connections_pending: number;
    paying: number; trialing: number; free: number; trials_ending_7d: number;
    activated_connections: number; activated_events: number;
  };
  plan_mix: Array<{ plan: AdminPlan; plan_status: AdminPlanStatus; n: number }>;
  mrr_cents: number;
  signups: Array<{ day: string; accounts: number; users: number }>;
};

export type AdminAccountRow = {
  id: string;
  name: string;
  email: string;
  created_at: string;
  plan: AdminPlan;
  plan_status: AdminPlanStatus;
  trial_ends_at: string | null;
  disabled_at: string | null;
  products: number;
  members: number;
  connections_ok: number;
  connections_error: number;
  connections_pending: number;
  last_activity: string | null;
};

export type AdminAccountListOpts = {
  q?: string;
  plan?: AdminPlan;
  status?: "disabled" | "trialing" | "paying" | "free";
  sort?: "created_at" | "name" | "plan" | "last_activity";
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type AdminAccountList = {
  accounts: AdminAccountRow[];
  total: number;
  limit: number;
  offset: number;
};

export type AdminAccountDetail = {
  account: {
    id: string;
    name: string;
    email: string;
    role: "customer" | "operator";
    created_at: string;
    verified_at: string | null;
    disabled_at: string | null;
    plan: AdminPlan;
    plan_status: AdminPlanStatus;
    trial_ends_at: string | null;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    plan_period_end: string | null;
  };
  members: Array<{
    id: string;
    email: string;
    name: string | null;
    member_role: "owner" | "member";
    accepted_at: string | null;
    disabled_at: string | null;
    verified_at: string | null;
    last_seen_at: string | null;
  }>;
  products: Array<{
    id: string;
    slug: string;
    name: string;
    stage: string;
    currency: string;
    trial_ends_at: string | null;
    created_at: string;
    spend_30d_cents: number;
    visits_30d: number;
    leads_30d: number;
    revenue_30d_cents: number;
  }>;
  connections: Array<{
    id: string;
    product_id: string;
    product_slug: string;
    kind: string;
    status: string;
    last_sync_at: string | null;
    last_error: string | null;
    syncing: boolean;
    sync_phase: string | null;
  }>;
  audit_log: Array<{
    created_at: string;
    actor: string;
    via: string;
    action: string;
    product_id: string | null;
    subject_ref: string | null;
  }>;
};

export type AdminUserRow = {
  id: string;
  email: string;
  name: string | null;
  verified_at: string | null;
  disabled_at: string | null;
  created_at: string;
  google_linked: boolean;
  last_seen_at: string | null;
  admin: boolean;
  source: AdminSource | null;
  accounts: Array<{ id: string; name: string; member_role: "owner" | "member" }>;
};

export type AdminUserList = {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
};

export type Billing = {
  plan: "free" | "indie" | "growth";
  stored_plan: "free" | "indie" | "growth";
  plan_status: "active" | "trialing" | "past_due" | "canceled";
  trial_ends_at: string | null;
  trial_days_left: number | null;
  plan_period_end: string | null;
  billing_available: boolean;
  has_customer: boolean;
  subscribed: boolean;
  member_role: "owner" | "member" | null;
  period: string;
  usage: { products: number; seats: number; events: number; analyses: number };
  limits: { products: number | null; seats: number | null; events: number; analyses: number; history_months: number | null };
  features: { ai_wire_up: boolean; ai_advanced: boolean; email_digest: boolean; csv_export: boolean };
  plans: Array<{ id: "free" | "indie" | "growth"; name: string; price_cents: number }>;
  /** Projects past their own 30-day trial — the subscription quantity. */
  billable_products: number;
  quantity: number;
  product_lines: Array<{
    slug: string;
    name: string;
    trial_ends_at: string | null;
    trial_days_left: number | null;
    billable: boolean;
    events: number;
    analyses: number;
  }>;
};

export type TrackingKey = {
  key: string | null;
  created_at: string | null;
  snippet: string | null;
  gtm_html: string | null;
  curl_example: string;
  ingest_url: string;
  gtm_connected: boolean;
  gtm_deploy: { deployed_at: string; version_name?: string; fk_snippet_found?: boolean } | null;
};

/** `product_slug` is the flow's product: this screen is reached by Google's
 *  redirect, so it is the only thing that still knows which one it was. */
export type ConnectStatus = { product_slug: string } & (
  | { status: "pending" }
  | { status: "error"; error: string }
  | {
      status: "complete";
      results: Array<{ kind: string; connection_id: string; status: string }>;
      options: {
        ga4_properties?: Array<{ id: string; name: string }>;
        gtm_containers?: Array<{ path: string; name: string; public_id: string }>;
        ads_customers?: Array<{ id: string; name: string; currency: string; manager: boolean }>;
        /** Set when Ads accounts can't be listed yet (token pending, test-account
         *  access, or a discovery failure): type the customer id instead. */
        ads_blocked?: "developer_token_pending" | "developer_token_test_access" | "discovery_failed";
        ads_error?: string;
        discovery_error?: string;
        gsc_sites?: Array<{ site_url: string }>;
        gsc_error?: string;
        meta_ad_accounts?: Array<{ id: string; name: string; currency: string }>;
        meta_pages?: Array<{ id: string; name: string; instagram_id: string | null; instagram_name: string | null }>;
      };
    }
);
